import React, { ChangeEvent, DOMAttributes, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  flattenOptions,
  groupOptions,
  IOptionGroup,
  ISelectState,
  ISelectStateProps,
  ISelectStatePropsProps,
  ISelectStatePropsValue,
  isIOption,
  SelectState,
} from '@hse-design/core';

import { useClickOutside, useFactoryRef, useMixinClassRef } from '../../common/hooks';
import { usePropsRef } from '../../common/hooks/usePropsRef';
import { ISelectOption } from './types';

type RootHandlers = Pick<
  DOMAttributes<HTMLDivElement>,
  'onKeyDown' | 'onFocus' | 'onBlur'
>;

export interface UseSelectHandlers extends RootHandlers {
  onClearClick?: () => void;
  onClearFocus: (e: React.FocusEvent<HTMLButtonElement>) => void;
  onClearBlur: () => void;
  onValueClick: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchInputFocus: () => void;
  onSearchInputBlur: () => void;
  createOptionClickHandler: (option: ISelectOption) => () => void;
  createOptionMouseEnterHandler: (option: ISelectOption) => () => void;
  onClickOutside?: (e: MouseEvent) => void;
}

export type UseSelectStateArgs = {
  options: (ISelectOption | IOptionGroup<ISelectOption>)[];
  rootHandlers: RootHandlers;
} & Omit<ISelectStatePropsProps<ISelectOption>, 'options'> & ISelectStatePropsValue<ISelectOption>;

export interface UseSelectRefs {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  searchInputRef: React.MutableRefObject<HTMLInputElement | null>;
  dropdownRef: React.MutableRefObject<HTMLDivElement | null>;
  multipleValuesRef: React.MutableRefObject<HTMLDivElement | null>;
  dropdownMenuItemsRef: React.MutableRefObject<(HTMLDivElement | null)[]>;
  dropdownMenuItemsContainerRef: React.MutableRefObject<
    HTMLDivElement | null
  >;
}

export function useSelectState(
  props: UseSelectStateArgs
): readonly [
  ISelectState<ISelectOption>,
  UseSelectRefs,
  UseSelectHandlers,
  (ISelectOption | IOptionGroup<ISelectOption>)[],
  SelectState<ISelectOption>
] {
  const {
    options: propOptions,
    rootHandlers: { onKeyDown, onFocus, onBlur }
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const multipleValuesRef = useRef<HTMLDivElement>(null);
  const dropdownMenuItemsContainerRef = useRef<HTMLDivElement>(null);
  const dropdownMenuItemsRef = useRef<HTMLDivElement[]>([]);

  // Если в проп options передан массив групп опций, разворачиваем его в массив опций, т.к. структура групп на логику никак не влияет
  const options = useMemo(() => flattenOptions(propOptions), [propOptions]);

  const [state, setState] = useState<ISelectState<ISelectOption>>(() => ({
    ...SelectState.initialState
  }));

  const selectState = useMixinClassRef(() => new SelectState<ISelectOption>(setState));

  const propsRef = usePropsRef({
    onKeyDown,
    onFocus,
    onBlur
  });

  const funcsRef = useFactoryRef(() => ({
    onClearClick: () => {
      selectState.onClearClick();
    },
    onClearFocus: (e: React.FocusEvent<HTMLButtonElement>) => {
      selectState.onClearFocus(e.nativeEvent);
    },
    onClearBlur: selectState.onClearBlur,
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      selectState.onKeyDown(e.nativeEvent);
      propsRef.onKeyDown?.(e);
    },
    onFocus: (e: React.FocusEvent<HTMLDivElement>) => {
      selectState.onFocus();
      propsRef.onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
      if (e.target === containerRef.current) {
        selectState.onBlur();
      }
      propsRef.onBlur?.(e);
    },
    onValueClick: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
      selectState.onValueClick(ev.nativeEvent),
    onSearchChange: (e: ChangeEvent<HTMLInputElement>) =>
      selectState.onSearchChange(e.target.value),
    onSearchInputFocus: () => selectState.onSearchInputFocus(),
    onSearchInputBlur: () => selectState.onSearchInputBlur(),
    createOptionClickHandler: (option: ISelectOption) => () =>
      selectState.onOptionClick(option),
    createOptionMouseEnterHandler: (option: ISelectOption) => () =>
      selectState.onOptionMouseEnter(option),
    onClickOutside: (e: MouseEvent) => selectState.onClickOutside(e)
  }));

  const selectProps: ISelectStateProps<ISelectOption> = {
    selectedFirst: props.selectedFirst,
    permanent: props.permanent,
    searchable: props.searchable,
    clearable: props.clearable,
    options,
    getOptions: props.getOptions,

    // Getting around typescript
    ...(props.multiple
      ? {
        multiple: props.multiple,
        value: props.value,
        onChange: props.onChange,
        onChangeOption: props.onChangeOption,
        onClear: props.onClear,
      }
      : {
        multiple: props.multiple,
        value: props.value,
        onChange: props.onChange,
        onChangeOption: props.onChangeOption,
        onClear: props.onClear,
      })
  };

  selectState.compute(state, selectProps);

  useLayoutEffect(() => {
    selectState.update(
      state,
      selectProps,
      {
        container: containerRef.current,
        searchInput: searchInputRef.current,
        dropdown: dropdownRef.current,
        dropdownMenuItems: dropdownMenuItemsRef.current,
        dropdownMenuItemsContainer: dropdownMenuItemsContainerRef.current,
        multipleValues: multipleValuesRef.current
      }
    );
  });

  const groupedOptions = useMemo(() => groupOptions(state.filteredOptions), [
    state.filteredOptions
  ]);

  useClickOutside(containerRef, funcsRef.onClickOutside);

  return [
    state,
    {
      containerRef,
      searchInputRef,
      dropdownRef,
      multipleValuesRef,
      dropdownMenuItemsRef,
      dropdownMenuItemsContainerRef
    },
    funcsRef,
    groupedOptions,
    selectState
  ] as const;
}

export { isIOption, groupOptions };
